import React from 'react';
import { Modal, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import * as moment from 'moment';
import CloseIconWhite from 'assets/images/CloseIconWhite.png';
import { formatEquipmentRequirements } from 'helpers';

const ModalContentWrapper = styled.div`
  font-family: 'latoregular', sans-serif;
  color: #333333;
  padding: 0;

  .modal-header-custom {
    background-color: #0a5587;
    color: #ffffff;
    padding: 14px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;

    h5 {
      margin: 0;
      font-family: 'latobold', sans-serif;
      font-size: 18px;
      color: #ffffff;
      flex: 1;
      text-align: center;
      padding-left: 20px;
    }

    .close-btn {
      background: transparent;
      border: none;
      color: #ffffff;
      font-size: 20px;
      cursor: pointer;
      line-height: 1;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 14px;
        height: 14px;
      }
    }
  }

  .modal-body-custom {
    padding: 20px;
    background-color: #ffffff;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .summary-card {
    background-color: #fcfcfc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px;
    margin-bottom: 20px;

    .row-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      font-size: 14px;

      .order-num {
        font-family: 'latobold', sans-serif;
        color: #0a5587;
        span {
          color: #333333;
          font-family: 'latomedium', sans-serif;
        }
      }

      .po-num {
        font-family: 'latobold', sans-serif;
        color: #0a5587;
        margin-right: 100px;
        span {
          color: #333333;
          font-family: 'latomedium', sans-serif;
        }
      }
    }

    .row-bottom {
      font-size: 14px;
      display: flex;
      gap: 5px;
      .location-title {
        font-family: 'latobold', sans-serif;
        color: #0a5587;
        margin-bottom: 4px;
      }
      .location-val {
        color: #555555;
        font-family: 'latomedium', sans-serif;
      }
    }
  }

  .section-header {
    font-family: 'latobold', sans-serif;
    font-weight: bold;
    font-size: 14px;
    color: #000000;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }

  .delivery-info-card {
    background-color: #fcfcfc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 14px 20px;
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;

    .info-col {
      flex: 1;

      .info-label {
        font-size: 13px;
        font-family: 'latobold', sans-serif;
        text-transform: uppercase;
        margin-bottom: 4px;
      }

      .info-val {
        font-size: 13px;
        font-family: 'latomedium', sans-serif;
      }
    }
  }

  .table-responsive-wrapper {
    overflow-x: auto;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    margin-bottom: 20px;
  }

  .pallet-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    thead {
      th {
        padding: 12px 10px;
        font-family: 'latobold', sans-serif;
        text-align: left;
        border-bottom: 1px solid #e2e8f0;
        white-space: nowrap;
      }
    }

    tbody {
      tr {
        td {
          padding: 12px 10px;
          color: #333333;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
          white-space: nowrap;
        }
      }
    }
  }

  .footer-action {
    display: flex;
    justify-content: center;
    margin-top: 10px;

    .close-main-btn {
      background-color: #7cb342;
      color: #ffffff;
      font-family: 'latobold', sans-serif;
      font-size: 14px;
      border: none;
      border-radius: 6px;
      padding: 8px 40px;
      cursor: pointer;
      transition: background-color 0.2s ease-in-out;

      &:hover {
        background-color: #689f38;
      }
    }
  }
`;

const MixPalletDetailsModal = ({
  isOpen,
  toggle,
  orderData,
  loggedUserCountry,
  onlyBreakdown = false,
  isDashboard = false,
  title
}) => {
  if (!orderData) return null;

  const formatDeliveryLocation = (data) => {
    if (!data) return 'NA';
    const addr = data.address || data.addressDTO || data.contactInfo?.address || data.buyerInfo?.address || data.shippingAddress || {};
    const parts = [];

    if (addr.locationName || addr.name) {
      parts.push(addr.locationName || addr.name);
    }
    if (addr.address1 || addr.street || addr.line1) parts.push(addr.address1 || addr.street || addr.line1);
    if (addr.address2 || addr.line2) parts.push(addr.address2 || addr.line2);

    const stateVal = addr.stateAbv || addr.state;
    const cityStateZip = [addr.city, stateVal].filter(Boolean).join(', ');
    if (cityStateZip) {
      let csz = cityStateZip;
      if (addr.zipCode || addr.zip) csz += ` ${addr.zipCode || addr.zip}`;
      parts.push(csz);
    } else if (addr.zipCode || addr.zip) {
      parts.push(addr.zipCode || addr.zip);
    }

    return parts.length ? parts.join(', ') : 'NA';
  };

  const getEquipmentString = (data) => formatEquipmentRequirements(data);

  const getRequestedWindow = (data) => {
    if (!data) return 'NA';

    const rawStart = data.startTime
      || data.requestedStartTime
      || data.contactInfo?.requestedStartTime
      || data.contactInfo?.startTime
      || data.rescheduleOrder?.requestedStartTime
      || data.deliveryFromTime
      || data.deliveryFromtime
      || data.loadDetails?.[0]?.deliveryFromtime
      || data.deliveryWindowBegin
      || data.orderDetails?.startTime;

    const rawEnd = data.endTime
      || data.requestedEndTime
      || data.contactInfo?.requestedEndTime
      || data.contactInfo?.endTime
      || data.rescheduleOrder?.requestedEndTime
      || data.deliveryToTime
      || data.deliveryTotime
      || data.loadDetails?.[0]?.deliveryTotime
      || data.deliveryWindowEnd
      || data.orderDetails?.endTime;

    if (!rawStart && !rawEnd) return 'NA';

    const formatTime = (t) => {
      if (!t || t === 'null') return '';
      const m = moment(t, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A', 'H:mm']);
      return m.isValid() ? m.format('HH:mm') : t;
    };

    const s = formatTime(rawStart);
    const e = formatTime(rawEnd);

    if (s && e) return `${s} To ${e}`;
    if (s) return s;
    if (e) return e;

    return 'NA';
  };

  const formatMaterialCondition = (palletDetails) => {
    if (!palletDetails) return 'NA';
    const mat = palletDetails.material || '';
    let cond = Array.isArray(palletDetails.condition)
      ? palletDetails.condition.join(', ')
      : palletDetails.condition || '';

    if (palletDetails.newConditionType === 'HARDWOOD') {
      cond += ' (HW)';
    } else if (palletDetails.newConditionType === 'SOFTWOOD') {
      cond += ' (SW)';
    }

    const result = [mat, cond].filter(Boolean).join(', ');
    return result || 'NA';
  };

  const formatDimensionValue = (val) => {
    if (val === undefined || val === null || val === '') return '';
    const num = Number(val);
    if (!isNaN(num)) {
      return String(num);
    }
    return String(val).trim();
  };

  const formatTypeSizeGrade = (palletDetails) => {
    if (!palletDetails) return 'NA';
    const pType = palletDetails.palletType || '';
    let sizeStr = palletDetails.size || '';

    if (sizeStr === 'Custom' || palletDetails.length || palletDetails.width || palletDetails.height || palletDetails.dimension) {
      const dimObj = palletDetails.dimension || palletDetails;
      if (dimObj?.length && dimObj?.width) {
        const len = formatDimensionValue(dimObj.length);
        const wid = formatDimensionValue(dimObj.width);
        const hgt = dimObj.height ? formatDimensionValue(dimObj.height) : '';
        const dimStr = `${len}x${wid}${hgt ? 'x' + hgt : ''}`;
        sizeStr = sizeStr && sizeStr !== 'Custom' ? `${sizeStr} (${dimStr})` : dimStr;
      }
    }

    const grade = typeof palletDetails.grade === 'string' ? palletDetails.grade : (palletDetails.grade?.gradeName || '');
    const result = [pType, sizeStr, grade].filter(Boolean).join(', ');
    return result || 'NA';
  };

  const reqDate = orderData.requestedDate
    || orderData.requestDeliveryDate
    || orderData.buyerDeliveryDate
    || orderData.sellerDeliveryDate
    || orderData.orderDetails?.postedOn
    || orderData.loadDetails?.[0]?.requestDeliveryDate;

  const palletCombinationsList = (Array.isArray(orderData.palletCombinations) && orderData.palletCombinations.length > 0)
    ? orderData.palletCombinations : [];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="mix-pallet-order-details-modal-style">
      <ModalContentWrapper>
        <div className="modal-header-custom">
          <h5>{title || "Order Details"}</h5>
          <button className="close-btn" onClick={toggle} aria-label="Close">
            <img src={CloseIconWhite} alt="close-icon" />
          </button>
        </div>

        <ModalBody className="modal-body-custom">
          {/* Top Summary Card */}
          {!(onlyBreakdown || isDashboard) && (
            <div className="summary-card">
              <div className="row-top">
                <div className="order-num">
                  Reference Number: <span>{orderData.orderRefNumber || orderData.orderNumber || orderData.orderNo || orderData.orderDetails?.title || orderData.id || orderData.refNo || 'NA'}</span>
                </div>
                <div className="po-num">
                  PO Number: <span>{orderData.poNumber || orderData.orderDetails?.poNumber || 'NA'}</span>
                </div>
              </div>
              <div className="row-bottom">
                <div className="location-title">Delivery Location:</div>
                <div className="location-val">{formatDeliveryLocation(orderData)}</div>
              </div>
            </div>
          )}

          {/* Delivery Info Section */}
          {!(onlyBreakdown || isDashboard) && (
            <>
              <div className="section-header">
                <b>DELIVERY INFO</b>
              </div>
              <div className="delivery-info-card">
                <div className="info-col">
                  <div className="info-label">DELIVERY DATE</div>
                  <div className="info-val">
                    {reqDate ? moment(reqDate).format('MM/DD/YYYY') : 'NA'}
                  </div>
                </div>
                <div className="info-col">
                  <div className="info-label">EQUIPMENT REQUIREMENTS</div>
                  <div className="info-val">
                    {getEquipmentString(orderData)}
                  </div>
                </div>
                <div className="info-col">
                  <div className="info-label">REQUESTED TIME WINDOW</div>
                  <div className="info-val">
                    {getRequestedWindow(orderData)}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Pallet Details Section */}
          <div className="section-header">
            <b>PALLET DETAILS (MIX PALLET BREAKDOWN)</b>
          </div>
          <div className="table-responsive-wrapper">
            <table className="pallet-table">
              <thead>
                <tr>
                  <th>Material/Condition</th>
                  <th>Type/Size/Grade</th>
                  <th>Moisture</th>
                  <th>HT</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                {palletCombinationsList.length > 0 ? (
                  palletCombinationsList.map((item, index) => {
                    const pDet = item.palletDetails || {};

                    return (
                      <tr key={index}>
                        <td>{formatMaterialCondition(pDet)}</td>
                        <td>{formatTypeSizeGrade(pDet)}</td>
                        <td>{pDet.moisture || 'NA'}</td>
                        <td>{pDet.heatTreared || pDet.heatTreated ? 'Yes' : 'No'}</td>
                        <td>{item.quantity ?? item.originalQuantity ?? 'NA'}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '20px' }}>
                      No mix pallet details found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Action */}
          <div className="footer-action">
            <button className="close-main-btn" onClick={toggle}>
              Close
            </button>
          </div>
        </ModalBody>
      </ModalContentWrapper>
    </Modal>
  );
};

export default MixPalletDetailsModal;
